import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/Button";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { useDevices, useGroups, useHasScenes, useKits, usePeople } from "@/lib/hooks/usePeople";
import { CALLABLES } from "@/services/callables";
import { COMPOSER_URL } from "@/lib/links";
import { ownerChecklist } from "./checklist";
import { isChecklistDismissed, setChecklistDismissed } from "./dismissal";
import type { LegalStatus } from "@/types";

/**
 * The owner's first afternoon, as a list.
 *
 * Five steps, each a link to the screen where it is done, each ticked by what
 * exists rather than by what was clicked. It can be put away for the visit and
 * comes back at the next sign-in until the last step is done; then it is gone
 * for good, because a checklist with every box ticked is furniture.
 */
export function OwnerChecklist() {
  const t = useT();
  const { user } = useAuth();
  const uid = user?.uid ?? "";
  const [dismissed, setDismissed] = useState(() => isChecklistDismissed(uid));

  const legal = useCallable<LegalStatus>(CALLABLES.getLegalStatus);
  const { people } = usePeople();
  const { groups } = useGroups();
  const { devices } = useDevices();
  const { kits } = useKits();
  const { hasScenes } = useHasScenes();

  const checklist = useMemo(
    () =>
      ownerChecklist({
        legal: legal.data,
        people,
        groups,
        devices,
        kits,
        hasScenes,
        composerUrl: COMPOSER_URL,
      }),
    [legal.data, people, groups, devices, kits, hasScenes]
  );

  if (checklist.complete) return null;

  if (dismissed) {
    return (
      <p className="mb-8 text-sm text-ink-muted">
        <button
          type="button"
          onClick={() => {
            setChecklistDismissed(uid, false);
            setDismissed(false);
          }}
          className="underline underline-offset-2 hover:text-ink"
        >
          {t("onboarding.showAgain")}
        </button>
      </p>
    );
  }

  return (
    <section aria-labelledby="onboarding-checklist" className="card mb-8 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 id="onboarding-checklist" className="section-title">
            {t("onboarding.title")}
          </h2>
          <p className="mt-1 text-sm text-ink-muted">{t("onboarding.intro")}</p>
          <p className="mt-1 text-xs text-ink-muted">
            {t("onboarding.progress", { done: checklist.doneCount, total: checklist.total })}
          </p>
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            setChecklistDismissed(uid, true);
            setDismissed(true);
          }}
        >
          {t("onboarding.dismiss")}
        </Button>
      </div>

      <ol className="mt-5 flex flex-col gap-3">
        {checklist.steps.map((step, index) => (
          <li key={step.id} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] ${
                step.done
                  ? "border-positive bg-positive text-white"
                  : "border-line bg-white text-ink-muted"
              }`}
            >
              {step.done ? "✓" : index + 1}
            </span>
            <div className="min-w-0">
              {step.done ? (
                <span className="text-sm text-ink-muted line-through">{t(step.labelKey)}</span>
              ) : step.external ? (
                <a href={step.to} className="text-sm font-medium text-ink hover:underline">
                  {t(step.labelKey)}
                  <span aria-hidden="true" className="ml-1 text-ink-faint">↗</span>
                </a>
              ) : (
                <Link to={step.to} className="text-sm font-medium text-ink hover:underline">
                  {t(step.labelKey)}
                </Link>
              )}
              {!step.done && step.hintKey ? (
                <p className="mt-0.5 text-sm text-ink-muted">{t(step.hintKey, step.hintParams)}</p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
